import { useEffect, useRef } from 'react'
import { useTexte } from '../../texte'

import { BergIcon, CollageIcon, GifIcon, KalenderIcon, NoteIcon, PhotoIcon, PinIcon, PollIcon, SonneIcon } from '../../components/Icons'

/**
 * Was an eine Nachricht kann — hinter **einem** Knopf.
 *
 * Aus dem Betrieb: „am Smartphone ist das Eingabefeld zu schmal: die Symbole
 * für Fotoupload und GIF enger zusammen oder untereinander oder durch ein +
 * ersetzen, und dann ein Auswahlfenster mit Foto/Video / Kamera / GIF."
 *
 * **Die Kamera ist wieder heraus**, und zwar auf Ansage: „die Kamera aus dem
 * Auswahlmenü herausnehmen (nur Foto/Video + GIF)." Sie brachte nichts, was
 * die Zeile darüber nicht auch kann -- die Foto-App eines Telefons hat einen
 * Auslöser -- und sie kam mit eigenen Sonderfällen: `capture="environment"`
 * ist der Weg, über den die gemeldeten 360p ins Haus kamen, und ein zweites
 * Dateifeld ist ein zweiter Weg zu derselben Sache.
 *
 * **Der Platz ist der Punkt.** Jeder Knopf in der Eingabezeile ist 44 Punkte
 * breit, weil er es sein muss; auf 390 Punkten bleibt für den Text, was die
 * Knöpfe übrig lassen. Zwei davon in einen zu legen gibt dem Feld 52 Punkte
 * zurück.
 *
 * **Es ist eine Liste und keine Reihe.** Eine Reihe aus 44-Punkt-Flächen ist
 * nach acht Stück breiter als ein iPhone; untereinander kann sie nicht
 * hinausragen, und jede Zeile trägt ihren Namen statt eines Symbols, das man
 * erst lernen müsste.
 *
 * **Das Blatt löst nichts selbst aus.** Es meldet, was gewählt wurde; die
 * Dateifelder liegen in der Eingabezeile. Das ist keine Förmlichkeit: auf iOS
 * öffnet ein Dateifeld nur, wenn der Griff aus einer echten Geste kommt, und
 * ein Tipp auf eine Zeile ist genau das — ein Umweg über einen Zustand wäre es
 * nicht mehr.
 */
export function Anhangwahl({
  darfDateien,
  nurFotos = false,
  nurFotosGrund,
  ausReise = false,
  gifs,
  abstimmung,
  wetter,
  wetterAnderswo = false,
  tag,
  musik = false,
  kartenlink = false,
  tagestrenner = false,
  collage = false,
  onFotoOderVideo,
  onCollage,
  onAusReise,
  onGif,
  onAbstimmung,
  onWetter,
  onWetterAnderswo,
  onTag,
  onMusik,
  onKartenlink,
  onTagestrenner,
  ohneNetz = false,
  onOhneNetz,
  onSchliessen,
}: {
  /** Ob die Zeile für Dateien überhaupt dasteht. */
  darfDateien: boolean
  /**
   * Nur Fotos, kein Video — der Fall des Mitlesenden (#59).
   *
   * Die Zeile sagt, was geht, statt etwas anzubieten und es danach
   * abzulehnen. Genau das war der Fehler, den es hier schon einmal gab.
   */
  nurFotos?: boolean
  /**
   * Warum nur Fotos — ein Satz unter der Zeile, wenn es einen gibt (1.45.0).
   *
   * Beim Mitlesenden braucht es ihn nicht: dort ist „Foto" der Normalfall
   * seiner Rolle. Ohne Netz dagegen steht am selben Platz sonst plötzlich ein
   * Wort weniger, und niemand wüsste, warum.
   */
  nurFotosGrund?: string
  /**
   * Pictures that hang at a point or spot already (1.52.0, from use: "auch
   * im Chat: Fotos aus dieser Reise") -- the traveller only; a reader has
   * nothing at a point.
   */
  ausReise?: boolean
  /** Ohne Schlüssel gibt es keine GIF-Suche und deshalb auch keine Zeile. */
  gifs: boolean
  /**
   * Eine Abstimmung anzulegen ist ein Beitrag an alle — das darf nur der
   * Autor, und nur im Reise-Chat. Im Verlauf mit einem Einzelnen wäre sie
   * eine Abstimmung mit einem Menschen.
   */
  abstimmung: boolean
  /** The current weather as a card (0.31.0) -- only for the traveller, only with access to Apple. */
  wetter: boolean
  /** The weather at a chosen place and hour (#83) -- needs the place search too, so Apple Maps as well. */
  wetterAnderswo?: boolean
  /** The day card (#79): the traveller only, and only where a route is. */
  tag: boolean
  /**
   * Musik (#102): nur der Autor / die Autorin, nur im Reise-Chat. Ohne Netz
   * steht sie ausgegraut da (1.80.0, `ohneNetz`) -- bis dahin war sie dann
   * ganz weg, als einzige.
   */
  musik?: boolean
  /** "Link zur Karte" (1.113.0): the author only, in the trip chat and its draft. */
  kartenlink?: boolean
  /**
   * The day separator (#113): the traveller only, only in the trip chat.
   * Works without a net -- nothing is asked anywhere, the line goes out
   * with the post, and the post goes into the outbox like any other.
   */
  tagestrenner?: boolean
  /** A collage of two to four photos (1.168.0): whoever may attach photos. Needs the net. */
  collage?: boolean
  onFotoOderVideo: () => void
  onCollage?: () => void
  onAusReise?: () => void
  onGif: () => void
  onAbstimmung: () => void
  onWetter: () => void
  onWetterAnderswo?: () => void
  onTag: () => void
  onMusik?: () => void
  onKartenlink?: () => void
  onTagestrenner?: () => void
  /**
   * No net right now (1.80.0). The entries that need one stand greyed with a
   * sentence, and a tap hands the reason to `onOhneNetz` instead of opening
   * anything. From use: "Musik wird ausgeblendet, wenn offline; andere
   * bleiben" -- decided: all stand, greyed. An entry that works without a
   * net (photo, current weather, poll) is not touched.
   */
  ohneNetz?: boolean
  onOhneNetz?: (grund: string) => void
  onSchliessen: () => void
}) {
  const kasten = useRef<HTMLDivElement>(null)

  const t = useTexte()
  useEffect(() => {
    function beiTaste(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onSchliessen()
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [onSchliessen])

  // **Ein Tipp daneben schließt.** Ohne das bliebe das Blatt stehen, bis
  // jemand es ausdrücklich zumacht -- und es deckt die halbe Eingabezeile.
  useEffect(() => {
    function beiTipp(event: PointerEvent) {
      const ziel = event.target
      if (ziel instanceof Node && kasten.current?.contains(ziel) === true) return
      onSchliessen()
    }
    // In der Aufnahmephase: ein Knopf darunter darf seine Sache trotzdem tun,
    // aber das Blatt schliesst dabei.
    window.addEventListener('pointerdown', beiTipp)
    return () => window.removeEventListener('pointerdown', beiTipp)
  }, [onSchliessen])

  /**
   * An entry that needs the net: greyed without one, and the tap says why.
   * `aria-disabled` and not `disabled` -- a disabled button takes no tap,
   * and a tap that does nothing is the silent refusal.
   */
  function netzZeile(
    daten: string | undefined,
    Symbol: typeof PhotoIcon,
    beschriftung: string,
    grund: string,
    los: (() => void) | undefined,
  ) {
    return (
      <button
        type="button"
        className={`anhangwahl__zeile${ohneNetz ? ' anhangwahl__zeile--ohne-netz' : ''}`}
        data-anhang={daten}
        aria-disabled={ohneNetz ? 'true' : undefined}
        data-ohne-netz={ohneNetz ? '' : undefined}
        onClick={() => (ohneNetz ? onOhneNetz?.(grund) : los?.())}
      >
        <Symbol size={20} />
        <span>
          {beschriftung}
          {ohneNetz && <span className="anhangwahl__grund">{t.chat.gehtNurMitNetz}</span>}
        </span>
      </button>
    )
  }

  return (
    <div
      ref={kasten}
      className="anhangwahl"
      role="dialog"
      aria-modal="true"
      aria-label={t.chat.wasAnhaengen}
    >
      {darfDateien && (
        <button type="button" className="anhangwahl__zeile" onClick={onFotoOderVideo}>
          <PhotoIcon size={20} />
          <span>
            {nurFotos ? t.chat.nurFoto : t.chat.fotoOderVideo}
            {nurFotosGrund !== undefined && (
              <span className="anhangwahl__grund">{nurFotosGrund}</span>
            )}
          </span>
        </button>
      )}
      {/* Right after the photos (1.168.0, from use: "im + Menü (nach Fotos)"). */}
      {collage && netzZeile('collage', CollageIcon, t.chat.collage.menue, t.chat.collage.ohneNetz, onCollage)}
      {ausReise && netzZeile('aus-reise', PhotoIcon, t.karte.formular.ausDieserReise, t.chat.ausReiseOhneNetz, onAusReise)}
      {gifs && netzZeile('gif', GifIcon, t.chat.gifAnhang, t.chat.gifOhneNetz, onGif)}
      {abstimmung && (
        <button type="button" className="anhangwahl__zeile" data-anhang="abstimmung" onClick={onAbstimmung}>
          <PollIcon size={20} />
          <span>{t.chat.abstimmung}</span>
        </button>
      )}
      {wetter && (
        <button type="button" className="anhangwahl__zeile" data-anhang="wetter" onClick={onWetter}>
          <SonneIcon size={20} />
          <span>{t.chat.wetter}</span>
        </button>
      )}
      {wetter && wetterAnderswo
        && netzZeile('wetter-anderswo', SonneIcon, t.chat.wetterAnderswo, t.chat.wetterOhneNetz, onWetterAnderswo)}
      {/* Before the day card (1.86.0, from use: „Tagestrenner vor Streckenstatistik"). */}
      {tagestrenner && (
        <button type="button" className="anhangwahl__zeile" data-anhang="tagestrenner" onClick={onTagestrenner}>
          <KalenderIcon size={20} />
          <span>{t.chat.tagestrenner.eintrag}</span>
        </button>
      )}
      {/* Between the day separator and the statistics (1.113.0, from use). */}
      {kartenlink && netzZeile('kartenlink', PinIcon, t.chat.kartenlink.eintrag, t.chat.kartenlink.ohneNetz, onKartenlink)}
      {tag && netzZeile('tag', BergIcon, t.chat.streckenstatistik, t.chat.tagOhneNetz, onTag)}
      {musik && netzZeile('musik', NoteIcon, t.chat.musik, t.chat.musikOhneNetz, onMusik)}
    </div>
  )
}

/**
 * Wie viele Wege es gibt — und damit, ob sich ein Sammelknopf lohnt.
 *
 * **Bei einem einzigen Weg gibt es keinen `+`.** Ein Knopf, der ein Blatt mit
 * genau einer Zeile öffnet, ist ein Griff mehr für nichts; dann steht der eine
 * Knopf wieder direkt da. Das betraf die Mitlesenden, solange sie nur GIFs
 * schicken durften; seit #59 haben auch sie zwei Wege, und der `+` steht damit
 * bei ihnen ebenfalls.
 */
export function wegeZaehlen(darfDateien: boolean, gifs: boolean, abstimmung: boolean, wetter = false, tag = false, wetterAnderswo = false, ausReise = false, musik = false, tagestrenner = false, kartenlink = false, collage = false): number {
  return (darfDateien ? 1 : 0) + (gifs ? 1 : 0) + (abstimmung ? 1 : 0) + (wetter ? 1 : 0) + (tag ? 1 : 0) + (wetter && wetterAnderswo ? 1 : 0) + (ausReise ? 1 : 0) + (musik ? 1 : 0) + (tagestrenner ? 1 : 0) + (kartenlink ? 1 : 0) + (collage ? 1 : 0)
}
